<script setup lang="ts">
import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import SpinnerIcon from "primevue/icons/spinner";
import Tag from "primevue/tag";
import { computed } from "vue";
import AddCircleIcon from "@/components/shared/icons/AddCircleIcon.vue";
import CheckIcon from "@/components/shared/icons/CheckIcon.vue";
import ClockIcon from "@/components/shared/icons/ClockIcon.vue";
import RestoreIcon from "@/components/shared/icons/RestoreIcon.vue";
import { ADD_ON_STATUS_DROPDOWN_OPTIONS } from "./constants";
import { addOnRequestsPresets } from "./styles";
import type { AddOnDropdownOption, AddOnRequestItem } from "./types";

defineOptions({ name: "AddOnRequestRow" });

const props = withDefaults(
  defineProps<{
    item: AddOnRequestItem;
    canEditStatus?: boolean;
    canArchive?: boolean;
    isRestoring?: boolean;
  }>(),
  {
    canEditStatus: false,
    canArchive: false,
    isRestoring: false,
  },
);

const emit = defineEmits<{
  (e: "change-status", status: AddOnDropdownOption): void;
  (e: "add-to-order"): void;
  (e: "restore"): void;
}>();

const viewMode = computed(() => (props.item.addedToOrder ? "added" : props.item.archived ? "archived" : "active"));
const isArchived = computed(() => viewMode.value === "archived");
const addOnDetailsTextClass = computed(() => (isArchived.value ? "text-[#8A8FA3]" : "text-gray-600"));
const submittedTextClass = computed(() => (isArchived.value ? "text-gray-500" : "text-gray-600"));

const isOptionDisabled = (option: AddOnDropdownOption): boolean => option === "Archived" && !props.canArchive;

const detailFields = computed(() =>
  [
    { testid: "add-on-request-date-label", value: props.item.dateLabel },
    { testid: "add-on-request-location-label", value: props.item.location },
    { testid: "add-on-request-duration-label", value: props.item.durationLabel },
    {
      testid: "add-on-request-price-label",
      value: props.item.priceLabel,
      class: isArchived.value ? "" : "font-semibold text-indigo-base",
    },
  ].filter((field) => field.value),
);

const onStatusChange = (event: { value: AddOnDropdownOption }): void => {
  if (event.value !== props.item.status) emit("change-status", event.value);
};
</script>

<template>
  <div
    v-if="viewMode === 'active' || viewMode === 'archived'"
    class="flex flex-col sm:flex-row sm:items-center gap-6 p-5 border border-gray-175 rounded-lg"
    :class="isArchived ? 'bg-gray-200' : 'bg-blue-100'"
    data-testid="add-on-request-row"
  >
    <div class="flex-1 min-w-0">
      <div v-if="isArchived" class="flex items-center gap-3 mb-1.5">
        <span class="text-15p font-bold text-gray-600">{{ item.name }}</span>
        <Tag rounded value="ARCHIVED" class="!text-11p !font-bold !tracking-wide !bg-gray-150 !text-gray-600" data-testid="add-on-request-archived-tag"/>
      </div>
      <router-link
        v-else
        :to="{ name: 'edit-event', params: { itemId: item.eventId } }"
        target="_blank"
        class="text-15p font-semibold text-teal-900 hover:underline"
        data-testid="add-on-request-event-link"
      >
        {{ item.name }}
      </router-link>

      <div class="flex flex-col gap-1 mt-2 text-sm sm:flex-row sm:flex-wrap sm:items-center sm:gap-3" :class="addOnDetailsTextClass">
        <template v-for="(field, index) in detailFields" :key="field.testid">
          <span v-if="index > 0" class="hidden text-gray-400 sm:inline">|</span>
          <span :data-testid="field.testid" :class="field.class">{{ field.value }}</span>
        </template>
      </div>
      <div class="flex items-center gap-2 mt-2 text-13p" :class="submittedTextClass">
        <ClockIcon class="h-3.5 w-3.5 flex-none" />
        <span data-testid="add-on-request-submitted-label">Request submitted {{ item.requestedAtLabel }}</span>
      </div>
    </div>

    <div v-if="!isArchived" class="flex flex-col sm:flex-row sm:items-end gap-4 sm:flex-none">
      <div class="flex-none">
        <div class="text-xs font-semibold tracking-wide text-gray-600 mb-1.5">STATUS</div>
        <Dropdown
          :model-value="item.status"
          :options="ADD_ON_STATUS_DROPDOWN_OPTIONS"
          :option-disabled="isOptionDisabled"
          :disabled="!canEditStatus"
          :pt="addOnRequestsPresets.dropdown"
          class="!h-11"
          data-testid="add-on-request-status-dropdown"
          @change="onStatusChange"
        />
      </div>

      <Button
        :disabled="!canEditStatus"
        data-testid="add-on-request-add-to-order-button"
        class="!h-11 w-full sm:w-auto whitespace-nowrap !text-15p !bg-teal-900 !border-teal-900 hover:!bg-teal-700 hover:!border-teal-700"
        @click="emit('add-to-order')"
      >
        <AddCircleIcon class="h-4 w-4 mr-2" />
        Add Excursion
      </Button>
    </div>

    <Button
      v-else
      outlined
      :disabled="!canArchive || isRestoring"
      data-testid="add-on-request-restore-button"
      class="whitespace-nowrap !text-sm !font-semibold !bg-white !text-gray-800 !border-gray-400 hover:!bg-blue-400"
      @click="emit('restore')"
    >
      <SpinnerIcon v-if="isRestoring" class="h-4 w-4 mr-2 animate-spin" />
      <RestoreIcon v-else class="h-4 w-4 mr-2" />
      Restore to {{ item.status }}
    </Button>
  </div>

  <div
    v-else-if="viewMode === 'added'"
    class="flex items-center gap-4 p-5 border border-gray-175 rounded-lg bg-success-100"
    data-testid="add-on-request-add-confirmation-row"
  >
    <span class="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-success-800 flex-none">
      <CheckIcon class="text-success-800" />
    </span>
    <div class="flex-1">
      <p class="text-15p text-indigo-base">
        <span class="font-bold" data-testid="add-on-request-confirmation-name-label">{{ item.name }}</span> was added to the order as
        <span class="font-bold" data-testid="add-on-request-confirmation-status-label">Fulfilled</span>.
      </p>
      <p class="text-13p text-gray-600 mt-0.5" data-testid="add-on-request-confirmation-requested-label">
        Requested {{ item.requestedAtLabel }} · now in the Excursions section below.
      </p>
    </div>
  </div>
</template>
